import React, { useState } from "react";
import useSWR from "swr";
import api, { money } from "@/lib/api";
import { fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { Search, Users, User, Plus, Pencil, Upload, X, Check, Trash2, Link2 } from "lucide-react";
import { BOOKING, ROOM_TYPE } from "@/lib/labels";
import { toast } from "sonner";
import GenderIcon from "@/components/GenderIcon";

const fetcher = (u) => api.get(u).then((r) => r.data);
const empty = {
  first_name: "", last_name: "", email: "", phone: "", country: "", language: "es",
  gender: "", birthdate: "",
  passport_number: "", passport_expiry: "", dietary: "", allergies: "", medical: "",
  emergency_contact: "", emergency_contact_phone: "",
  tour_id: "", booking_status: "lead", price: 0, room_type: "double", single_supplement: 0,
};

const GENDER_LABEL = { male: "Hombre", female: "Mujer", other: "Otro", prefer_not_to_say: "Prefiere no decirlo" };
const calcAge = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
};

export default function Clients() {
  const { data, mutate } = useSWR("/clients", fetcher);
  const { data: tours } = useSWR("/tours", fetcher);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState("__personal__");
  const [reservasEdit, setReservasEdit] = useState({}); // {participant_id: {room_type, price, single_supplement, partner_email, booking_status}}

  // Excel import state
  const [impOpen, setImpOpen] = useState(false);
  const [impLoading, setImpLoading] = useState(false);
  const [impPreview, setImpPreview] = useState(null); // { headers, rows, total_rows, fields, mapping }
  const [impResult, setImpResult] = useState(null);
  const [impFileName, setImpFileName] = useState("");
  const [impFile, setImpFile] = useState(null);

  const resetImport = () => {
    setImpPreview(null); setImpResult(null); setImpFileName(""); setImpLoading(false); setImpFile(null);
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      return toast.error("Sube un archivo .xlsx");
    }
    setImpLoading(true);
    setImpFileName(file.name);
    setImpFile(file);
    setImpResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImpPreview({ ...data, mapping: { ...(data.suggested_mapping || {}) } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo leer el Excel");
      setImpPreview(null);
    } finally { setImpLoading(false); }
  };

  const runImport = async () => {
    if (!impPreview || !impFile) return;
    setImpLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      fd.append("mapping", JSON.stringify(impPreview.mapping));
      const { data } = await api.post("/clients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImpResult(data);
      toast.success(`${data.created} creado(s) · ${data.updated} actualizado(s) · ${data.skipped} omitido(s)`);
      mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally { setImpLoading(false); }
  };

  const deleteClient = async (c) => {
    if (!c?.email) return;
    const hasBookings = (c.tours_count || 0) > 0;
    const msg = hasBookings
      ? `¿Eliminar a ${c.first_name} ${c.last_name}?\n\nTiene ${c.tours_count} reserva(s). Se eliminarán también sus reservas y pagos asociados.\n\nEsta acción no se puede deshacer desde la UI.`
      : `¿Eliminar a ${c.first_name} ${c.last_name}?\n\nEsta acción no se puede deshacer desde la UI.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/clients/by-email/${encodeURIComponent(c.email)}${hasBookings ? "?cascade=true" : ""}`);
      toast.success("Cliente eliminado");
      if (sel?.email === c.email) setSel(null);
      mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const saveEdit = async () => {
    if (!sel?.email) return;
    setSavingEdit(true);
    try {
      if (activeEditTab === "__personal__") {
        const cleaned = Object.fromEntries(Object.entries(editForm).filter(([, v]) => v !== ""));
        const scopePid = (sel.bookings || [])[0]?.participant_id;
        const qs = scopePid ? `?participant_id=${encodeURIComponent(scopePid)}` : "";
        await api.patch(`/clients/by-email/${encodeURIComponent(sel.email)}${qs}`, cleaned);
        toast.success("Datos personales actualizados");
        const merged = { ...sel, ...cleaned };
        setSel(merged);
      } else {
        // Save reserva only for that participant.
        const r = reservasEdit[activeEditTab] || {};
        const payload = {
          room_type: r.room_type || "double",
          price: Number(r.price) || 0,
          single_supplement: Number(r.single_supplement) || 0,
          partner_email: r.partner_email || "",
          booking_status: r.booking_status || "lead",
        };
        await api.patch(`/participants/${activeEditTab}`, payload);
        toast.success("Reserva actualizada");
        // Refresh selected client so reopening the dialog shows the saved values
        try {
          const { data: fresh } = await api.get(`/clients/by-email/${encodeURIComponent(sel.email)}`);
          if (fresh) setSel(fresh);
        } catch { /* ignore */ }
      }
      setEditing(false);
      mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSavingEdit(false); }
  };

  const filtered = (data || []).filter((c) => {
    const s = `${c.first_name} ${c.last_name} ${c.email} ${c.country || ""}`.toLowerCase();
    return q === "" || s.includes(q.toLowerCase());
  });

  const submit = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()) {
      return toast.error("Nombre, apellidos y email son obligatorios");
    }
    setSaving(true);
    try {
      if (form.tour_id) {
        // Con primera reserva -> POST /participants
        await api.post("/participants", { ...form, price: Number(form.price), single_supplement: Number(form.single_supplement) || 0 });
      } else {
        // Cliente sin tour aún -> POST /clients (sin datos de reserva)
        const { tour_id, room_type, price, booking_status, single_supplement, ...clientOnly } = form;
        await api.post("/clients", clientOnly);
      }
      toast.success("Cliente creado");
      setForm(empty); setOpen(false); mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Clientes</h1>
          <p className="text-slate-500 mt-1">{data?.length || 0} personas registradas · historial de expediciones.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          data-testid="copy-signup-link-btn"
          onClick={() => {
            const url = `${window.location.origin}/join`;
            navigator.clipboard.writeText(url).then(
              () => toast.success(`Enlace copiado: ${url}`),
              () => toast.error("No se pudo copiar el enlace")
            );
          }}
        >
          <Link2 size={16} className="mr-2" />Copiar enlace de auto-registro
        </Button>
        <Dialog open={impOpen} onOpenChange={(v) => { setImpOpen(v); if (!v) resetImport(); }}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="import-excel-btn"><Upload size={16} className="mr-2" />Importar Excel</Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Importar clientes desde Excel</DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                Arrastra o selecciona un archivo .xlsx (máx 5 MB). Verás una vista previa y podrás mapear las columnas antes de importar.
              </p>
            </DialogHeader>

            {/* Step 1: upload */}
            {!impPreview && (
              <label
                htmlFor="import-file"
                data-testid="import-drop"
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg py-14 cursor-pointer transition-colors ${
                  impLoading ? "opacity-60 pointer-events-none border-slate-300" : "border-slate-300 hover:border-[#026FCB]"
                }`}
                onDrop={(e) => { e.preventDefault(); handleImportFile(e.dataTransfer.files?.[0]); }}
                onDragOver={(e) => e.preventDefault()}
              >
                <Upload size={30} className="text-[#026FCB]" />
                <div className="mt-3 text-sm font-medium text-[#026FCB]">{impLoading ? "Leyendo Excel…" : "Suelta el .xlsx aquí o haz clic para elegir"}</div>
                <div className="text-xs text-slate-500 mt-1">La primera fila debe contener los nombres de columna (cabeceras)</div>
                <input id="import-file" type="file" accept=".xlsx" className="hidden" data-testid="import-file-input"
                       onChange={(e) => handleImportFile(e.target.files?.[0])} />
              </label>
            )}

            {/* Step 2: mapping + preview */}
            {impPreview && !impResult && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="mono text-slate-500">Archivo:</span> <b>{impFileName}</b>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="mono text-slate-500">{impPreview.total_rows} filas de datos</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="mono text-slate-500">Vista previa: {impPreview.rows.length}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetImport} data-testid="import-reset">
                    <X size={13} className="mr-1" />Cambiar archivo
                  </Button>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-[#25334D] overflow-x-auto">
                  <table className="w-full text-xs min-w-[720px]">
                    <thead className="bg-slate-50 dark:bg-[#1A2336]">
                      <tr>
                        {impPreview.headers.map((h, i) => (
                          <th key={i} className="text-left px-3 py-2 border-r last:border-r-0 border-slate-200 dark:border-[#25334D] min-w-[160px]">
                            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 truncate" title={h}>{h}</div>
                            <Select
                              value={impPreview.mapping[String(i)] || "__none__"}
                              onValueChange={(v) => {
                                const next = { ...impPreview.mapping };
                                if (v === "__none__") delete next[String(i)];
                                else {
                                  // remove any other column mapped to the same field (avoid duplicates)
                                  Object.keys(next).forEach((k) => { if (next[k] === v) delete next[k]; });
                                  next[String(i)] = v;
                                }
                                setImpPreview({ ...impPreview, mapping: next });
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs mt-1" data-testid={`import-map-${i}`}>
                                <SelectValue placeholder="— No importar —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— No importar —</SelectItem>
                                {Object.entries(impPreview.fields).map(([k, label]) => (
                                  <SelectItem key={k} value={k}>{label}{k === "email" ? " *" : ""}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {impPreview.rows.slice(0, 8).map((row, ri) => (
                        <tr key={ri} className="border-t border-slate-100 dark:border-[#25334D]/50">
                          {impPreview.headers.map((_, ci) => (
                            <td key={ci} className="px-3 py-2 border-r last:border-r-0 border-slate-100 dark:border-[#25334D]/50 truncate max-w-[220px]" title={row[ci]}>
                              {row[ci] || <span className="text-slate-400 mono">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 text-xs">
                  <b>Reglas:</b> la columna <b>Email</b> es obligatoria. Se omitirán filas sin email válido o clientes que ya tengan reservas activas.
                  Las columnas marcadas como "— No importar —" no se cargarán.
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={resetImport}>Cancelar</Button>
                  <Button
                    data-testid="import-run"
                    className="bg-[#026FCB] hover:bg-[#0358a5]"
                    onClick={runImport}
                    disabled={impLoading || !Object.values(impPreview.mapping).includes("email")}
                  >
                    {impLoading ? "Importando…" : `Importar ${impPreview.total_rows} fila(s)`}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 3: result */}
            {impResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] mono uppercase text-slate-500">Total</div>
                    <div className="text-xl font-bold mt-0.5">{impResult.total}</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] mono uppercase text-slate-500">Creados</div>
                    <div className="text-xl font-bold mt-0.5 text-emerald-600">{impResult.created}</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] mono uppercase text-slate-500">Actualizados</div>
                    <div className="text-xl font-bold mt-0.5 text-sky-600">{impResult.updated}</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] mono uppercase text-slate-500">Omitidos</div>
                    <div className="text-xl font-bold mt-0.5 text-amber-600">{impResult.skipped}</div>
                  </CardContent></Card>
                </div>
                {(impResult.errors || []).length > 0 && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 p-3 max-h-56 overflow-y-auto">
                    <div className="text-xs mono uppercase text-amber-800 dark:text-amber-300 mb-2">
                      Filas omitidas ({impResult.errors.length}):
                    </div>
                    <ul className="text-xs space-y-1">
                      {impResult.errors.map((e, i) => (
                        <li key={i} className="mono"><b>Fila {e.row}:</b> {e.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={resetImport}>Importar otro archivo</Button>
                  <Button data-testid="import-close" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={() => { setImpOpen(false); resetImport(); }}>
                    <Check size={14} className="mr-1" />Cerrar
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nuevo cliente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo cliente</DialogTitle>
              <p className="text-sm text-slate-500 mt-1">Rellena los datos personales y la primera reserva. Podrás añadir más tours a este cliente después.</p>
            </DialogHeader>
            <div className="space-y-5">
              <div>
                <h3 className="text-xs mono uppercase tracking-widest text-[#026FCB] mb-2">Datos personales</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nombre *</Label><Input data-testid="client-first" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
                  <div><Label>Apellidos *</Label><Input data-testid="client-last" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
                  <div><Label>Email *</Label><Input data-testid="client-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                  <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
                  <div><Label>País</Label><Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} /></div>
                  <div><Label>Género</Label>
                    <Select value={form.gender || "__none__"} onValueChange={(v) => setForm({...form, gender: v === "__none__" ? "" : v})}>
                      <SelectTrigger data-testid="client-gender"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin especificar —</SelectItem>
                        <SelectItem value="male">Hombre</SelectItem>
                        <SelectItem value="female">Mujer</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefiere no decirlo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha de nacimiento{calcAge(form.birthdate) != null && <span className="ml-2 text-xs mono text-[#026FCB]">({calcAge(form.birthdate)} años)</span>}</Label>
                    <DateInputES data-testid="client-birthdate" value={form.birthdate} onChange={(e) => setForm({...form, birthdate: e.target.value})} />
                  </div>
                  <div><Label>Pasaporte</Label><Input value={form.passport_number} onChange={(e) => setForm({...form, passport_number: e.target.value})} /></div>
                  <div><Label>Caduca</Label><DateInputES value={form.passport_expiry} onChange={(e) => setForm({...form, passport_expiry: e.target.value})} /></div>
                  <div><Label>Persona de contacto</Label><Input placeholder="Nombre y parentesco" value={form.emergency_contact} onChange={(e) => setForm({...form, emergency_contact: e.target.value})} /></div>
                  <div><Label>Teléfono de contacto</Label><Input placeholder="+34 600 000 000" value={form.emergency_contact_phone} onChange={(e) => setForm({...form, emergency_contact_phone: e.target.value})} /></div>
                  <div className="col-span-2"><Label>Dieta / alergias / condiciones médicas</Label>
                    <Input placeholder="Dieta" className="mb-2" value={form.dietary} onChange={(e) => setForm({...form, dietary: e.target.value})} />
                    <Input placeholder="Alergias" className="mb-2" value={form.allergies} onChange={(e) => setForm({...form, allergies: e.target.value})} />
                    <Input placeholder="Condiciones médicas" value={form.medical} onChange={(e) => setForm({...form, medical: e.target.value})} />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xs mono uppercase tracking-widest text-[#026FCB] mb-2">Primera reserva (opcional)</h3>
                <p className="text-xs text-slate-500 mb-3">Si aún no tiene tour asignado, deja este bloque vacío y solo se guardará la ficha personal.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Tour</Label>
                    <Select value={form.tour_id || "__none__"} onValueChange={(v) => {
                      const tid = v === "__none__" ? "" : v;
                      const tp = (tours || []).find((t) => t.id === tid)?.price_per_participant;
                      setForm({
                        ...form,
                        tour_id: tid,
                        price: (!form.price || Number(form.price) === 0) && tp ? tp : form.price,
                      });
                    }}>
                      <SelectTrigger data-testid="client-tour"><SelectValue placeholder="Sin tour por ahora…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin tour por ahora —</SelectItem>
                        {(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Habitación</Label>
                    <Select value={form.room_type} onValueChange={(v) => setForm({...form, room_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(ROOM_TYPE).map((r) => <SelectItem key={r} value={r}>{ROOM_TYPE[r]}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400 mt-1">Solo aplica al añadirlo al tour.</p>
                  </div>
                  <div><Label>Precio pactado</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
                  <div className="col-span-2"><Label>Estado inicial</Label>
                    <Select value={form.booking_status} onValueChange={(v) => setForm({...form, booking_status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="client-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Crear cliente"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="clients-search" className="pl-9" placeholder="Buscar cliente…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-100 dark:bg-[#1A2336]">
            <tr>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">País</th>
              <th className="text-center px-4 py-3">Expediciones</th>
              <th className="text-right px-4 py-3">Total facturado</th>
              <th className="text-right px-4 py-3">Total pagado</th>
              <th className="text-right px-4 py-3 w-16">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.email} data-testid={`client-row-${c.email}`} className="border-t border-slate-100 dark:border-[#25334D]/50 hover:bg-slate-50 dark:hover:bg-[#1A2336]/60 cursor-pointer" onClick={() => setSel(c)}>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><GenderIcon gender={c.gender} size={12} />{c.first_name} {c.last_name}</td>
                <td className="px-4 py-3 text-slate-500">{c.email}</td>
                <td className="px-4 py-3 text-slate-500">{c.country || "—"}</td>
                <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 mono text-[#026FCB] font-semibold">{c.tours_count}</span></td>
                <td className="px-4 py-3 text-right mono">{money(c.total_billed)}</td>
                <td className="px-4 py-3 text-right mono text-emerald-600">{money(c.total_paid)}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`delete-client-${c.email}`}
                    onClick={() => deleteClient(c)}
                    title="Eliminar cliente"
                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">Sin clientes. Pulsa <b>+ Nuevo cliente</b> para crear el primero.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={!!sel} onOpenChange={(o) => { if (!o) { setSel(null); setEditing(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2"><Users size={18} />{sel?.first_name} {sel?.last_name}</DialogTitle>
            {sel && !editing && (
              <div className="flex items-center gap-1 mr-8">
                <Button size="sm" variant="outline" data-testid="edit-client-btn" onClick={() => {
                  setEditForm({
                    first_name: sel.first_name || "", last_name: sel.last_name || "",
                    phone: sel.phone || "", country: sel.country || "",
                    gender: sel.gender || "", birthdate: sel.birthdate || "",
                    passport_number: sel.passport_number || "", passport_expiry: sel.passport_expiry || "",
                    dietary: sel.dietary || "", allergies: sel.allergies || "", medical: sel.medical || "",
                    emergency_contact: sel.emergency_contact || "",
                    emergency_contact_phone: sel.emergency_contact_phone || "",
                  });
                  // Preload reservas per booking
                  const r = {};
                  for (const b of (sel.bookings || [])) {
                    const effPrice = (b.price && Number(b.price) > 0) ? b.price : (b.tour_price || 0);
                    r[b.participant_id] = {
                      room_type: b.room_type || "double",
                      price: effPrice,
                      single_supplement: b.single_supplement || 0,
                      partner_email: b.partner_email || "",
                      booking_status: b.booking_status || "lead",
                    };
                  }
                  setReservasEdit(r);
                  setActiveEditTab("__personal__");
                  setEditing(true);
                }}><Pencil size={14} className="mr-1" />Editar</Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="delete-client-detail-btn"
                  onClick={() => deleteClient(sel)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 border-red-200 dark:border-red-900/60"
                >
                  <Trash2 size={14} className="mr-1" />Eliminar
                </Button>
              </div>
            )}
          </DialogHeader>
          {sel && !editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-xs mono uppercase text-slate-500">Email</span><div>{sel.email}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Teléfono</span><div>{sel.phone || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">País</span><div>{sel.country || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Género</span><div>{GENDER_LABEL[sel.gender] || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Edad</span><div>{calcAge(sel.birthdate) != null ? `${calcAge(sel.birthdate)} años` : "—"}<span className="ml-2 text-xs mono text-slate-400">{sel.birthdate ? fmtDate(sel.birthdate) : ""}</span></div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Persona de contacto</span><div>{sel.emergency_contact || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Tel. contacto</span><div className="mono">{sel.emergency_contact_phone || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Dieta</span><div>{sel.dietary || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Alergias</span><div>{sel.allergies || "—"}</div></div>
                <div className="col-span-2"><span className="text-xs mono uppercase text-slate-500">Médico</span><div>{sel.medical || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Pasaporte</span><div className="mono">{sel.passport_number || "—"}</div></div>
                <div><span className="text-xs mono uppercase text-slate-500">Pasaporte caduca</span><div className="mono">{fmtDate(sel.passport_expiry)}</div></div>
              </div>
              <div>
                {(() => {
                  const todayISO = new Date().toISOString().slice(0,10);
                  const upcoming = (sel.bookings || []).filter((b) => (b.tour_start || "9999-12-31") >= todayISO && b.booking_status !== "cancelled" && b.booking_status !== "refunded");
                  const done = (sel.bookings || []).filter((b) => (b.tour_start || "") < todayISO || b.booking_status === "cancelled" || b.booking_status === "refunded");
                  return (
                    <>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">Próximas expediciones <span className="text-xs mono text-slate-500">({upcoming.length})</span></h3>
                      <div className="space-y-2" data-testid="client-upcoming">
                        {upcoming.length === 0 && <div className="text-sm text-slate-500">Sin reservas próximas.</div>}
                        {upcoming.map((b) => (
                          <div key={b.participant_id} className="flex items-center justify-between border border-slate-200 dark:border-[#25334D] rounded-lg px-3 py-2 text-sm">
                            <div>
                              <div className="font-medium">{b.tour_name}</div>
                              <div className="text-xs text-slate-500 mono">{fmtDate(b.tour_start)} · {b.tour_country}</div>
                            </div>
                            <div className="text-right">
                              <StatusBadge value={b.booking_status} />
                              <div className="text-xs mono mt-1">{money(b.paid)} / {money((Number(b.price) > 0 ? b.price : b.tour_price) || 0)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <h3 className="font-semibold mt-5 mb-2 flex items-center gap-2">Tours realizados <span className="text-xs mono text-slate-500">({done.length})</span></h3>
                      <div className="space-y-2" data-testid="client-done">
                        {done.length === 0 && <div className="text-sm text-slate-500">Aún no ha viajado con IceMonkey.</div>}
                        {done.map((b) => (
                          <div key={b.participant_id} className="flex items-center justify-between border border-slate-200 dark:border-[#25334D] rounded-lg px-3 py-2 text-sm bg-slate-50 dark:bg-[#1A2336]/40">
                            <div>
                              <div className="font-medium">{b.tour_name}</div>
                              <div className="text-xs text-slate-500 mono">{fmtDate(b.tour_start)} · {b.tour_country}</div>
                            </div>
                            <div className="text-right">
                              <StatusBadge value={b.booking_status} />
                              <div className="text-xs mono mt-1">{money(b.paid)} / {money((Number(b.price) > 0 ? b.price : b.tour_price) || 0)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          {sel && editing && (
            <div className="space-y-4">
              <Tabs value={activeEditTab} onValueChange={setActiveEditTab}>
                <TabsList className="flex-wrap h-auto">
                  <TabsTrigger value="__personal__" data-testid="edit-tab-personal">Datos personales</TabsTrigger>
                  {(sel.bookings || []).map((b) => (
                    <TabsTrigger key={b.participant_id} value={b.participant_id} data-testid={`edit-tab-${b.participant_id}`}>
                      {b.tour_name}
                      {b.tour_start && <span className="ml-2 text-[10px] mono opacity-60">{fmtDate(b.tour_start)}</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="__personal__" className="mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nombre</Label><Input data-testid="edit-first" value={editForm.first_name || ""} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} /></div>
                    <div><Label>Apellidos</Label><Input data-testid="edit-last" value={editForm.last_name || ""} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} /></div>
                    <div><Label>Teléfono</Label><Input value={editForm.phone || ""} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} /></div>
                    <div><Label>País</Label><Input value={editForm.country || ""} onChange={(e) => setEditForm({...editForm, country: e.target.value})} /></div>
                    <div><Label>Género</Label>
                      <Select value={editForm.gender || "__none__"} onValueChange={(v) => setEditForm({...editForm, gender: v === "__none__" ? "" : v})}>
                        <SelectTrigger data-testid="edit-gender"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin especificar —</SelectItem>
                          <SelectItem value="male">Hombre</SelectItem>
                          <SelectItem value="female">Mujer</SelectItem>
                          <SelectItem value="other">Otro</SelectItem>
                          <SelectItem value="prefer_not_to_say">Prefiere no decirlo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fecha de nacimiento{calcAge(editForm.birthdate) != null && <span className="ml-2 text-xs mono text-[#026FCB]">({calcAge(editForm.birthdate)} años)</span>}</Label>
                      <DateInputES data-testid="edit-birthdate" value={editForm.birthdate || ""} onChange={(e) => setEditForm({...editForm, birthdate: e.target.value})} />
                    </div>
                    <div><Label>Pasaporte</Label><Input value={editForm.passport_number || ""} onChange={(e) => setEditForm({...editForm, passport_number: e.target.value})} /></div>
                    <div><Label>Caducidad pasaporte</Label><DateInputES value={editForm.passport_expiry || ""} onChange={(e) => setEditForm({...editForm, passport_expiry: e.target.value})} /></div>
                    <div><Label>Persona de contacto</Label><Input placeholder="Nombre y parentesco" value={editForm.emergency_contact || ""} onChange={(e) => setEditForm({...editForm, emergency_contact: e.target.value})} /></div>
                    <div><Label>Teléfono de contacto</Label><Input placeholder="+34 600 000 000" value={editForm.emergency_contact_phone || ""} onChange={(e) => setEditForm({...editForm, emergency_contact_phone: e.target.value})} /></div>
                    <div className="col-span-2"><Label>Dieta</Label><Input value={editForm.dietary || ""} onChange={(e) => setEditForm({...editForm, dietary: e.target.value})} /></div>
                    <div className="col-span-2"><Label>Alergias</Label><Input value={editForm.allergies || ""} onChange={(e) => setEditForm({...editForm, allergies: e.target.value})} /></div>
                    <div className="col-span-2"><Label>Notas médicas</Label><Input value={editForm.medical || ""} onChange={(e) => setEditForm({...editForm, medical: e.target.value})} /></div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">Los datos personales se aplican a todas las reservas del cliente. El email no puede modificarse aquí.</p>
                </TabsContent>

                {(sel.bookings || []).map((b) => {
                  const r = reservasEdit[b.participant_id] || {};
                  const setR = (patch) => setReservasEdit((prev) => ({ ...prev, [b.participant_id]: { ...prev[b.participant_id], ...patch } }));
                  return (
                    <TabsContent key={b.participant_id} value={b.participant_id} className="mt-4">
                      <div className="mb-3 p-2 rounded-md bg-sky-50 dark:bg-sky-950/30 text-xs text-sky-800 dark:text-sky-300">
                        Editando reserva de <b>{b.tour_name}</b>{b.tour_start && <> · Inicio {fmtDate(b.tour_start)}</>}. Estos cambios solo afectan a este tour.
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Habitación</Label>
                          <Select value={r.room_type} onValueChange={(v) => setR({ room_type: v, ...(v !== "single" ? { single_supplement: 0 } : {}) })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.keys(ROOM_TYPE).map((rt) => <SelectItem key={rt} value={rt}>{ROOM_TYPE[rt]}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Precio pactado</Label><Input type="number" value={r.price ?? 0} onChange={(e) => setR({ price: e.target.value })} /></div>
                        {r.room_type === "single" && (
                          <div className="col-span-2"><Label>Suplemento habitación simple</Label>
                            <Input type="number" placeholder="Extra que paga por dormir solo" value={r.single_supplement || 0} onChange={(e) => setR({ single_supplement: e.target.value })} />
                          </div>
                        )}
                        <div className="col-span-2"><Label>Estado</Label>
                          <Select value={r.booking_status} onValueChange={(v) => setR({ booking_status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","refunded","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2"><Label>Pareja (comparte doble, ignora género)</Label>
                          <Input value={r.partner_email || ""} onChange={(e) => setR({ partner_email: e.target.value })} placeholder="email de pareja" />
                          <p className="text-[10px] text-slate-500 mt-1">Escribe el email de otro participante del mismo tour. Vacío = sin pareja.</p>
                        </div>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
                <Button data-testid="edit-save-btn" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEdit} disabled={savingEdit}>
                  {savingEdit ? "Guardando…" : (activeEditTab === "__personal__" ? "Guardar datos personales" : "Guardar reserva")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
